/**
 * Lingua dell'interfaccia: una sola per la scheda, scelta sulla postazione.
 */
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { setFormatLocale } from '../format'
import { currentLocale, readStored, writeStored, type Locale } from './locale'
import type { MessageKey } from './messages'
import { t as translate } from './t'

interface LocaleState {
  locale: Locale
  setLocale: (next: Locale) => void
}

// Senza un fornitore sopra — succede nelle prove che montano un componente da
// solo — la lingua e' quella in vigore sulla scheda, non una costante: cosi'
// la lingua che le prove fissano vale anche dove nessuno l'ha passata.
const LocaleContext = createContext<LocaleState | null>(null)

function apply(locale: Locale): void {
  setFormatLocale(locale)
  document.documentElement.lang = locale
}

export function LocaleProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const found = readStored()
    apply(found)
    return found
  })

  const value = useMemo(
    () => ({
      locale,
      setLocale: (next: Locale) => {
        writeStored(next)
        apply(next)
        setLocaleState(next)
      },
    }),
    [locale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleState {
  const found = useContext(LocaleContext)
  return found ?? { locale: currentLocale(), setLocale: () => {} }
}

export function useT(): (
  key: MessageKey,
  vars?: Record<string, string | number>,
) => string {
  const { locale } = useLocale()
  return (key, vars) => translate(locale, key, vars)
}
